





























































import { computed, defineComponent, PropType, ref, set } from "@vue/composition-api";
import { TranslateResult } from "vue-i18n";
import { RecurringBillingScheduleSubjectTypeEnum } from "@scrile/api-provider/dist/api/RecurringBillingScheduleProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import useAuthUser from "@/hooks/useAuthUser";
import { useMaxCurrentSubscription } from "@/hooks/useMaxCurrentSubscription";
import usePurchaseSubscription from "@/hooks/usePurchaseSubscription";
import useSystemSettings from "@/hooks/useSystemSettings";
import { yMetrics } from "@/lib/analytics";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString, FullPriceData, getFullPriceData, getMainCurrencyCode } from "@/lib/moneyHelpers";
import { getAvailableSystems } from "@/lib/paymentSystemsHelpers";
import { SubscriptionDetails } from "@/payments/types";
import { EVENTS, PurchaseType, SUBSCRIPTION_LEVEL_FRAGMENT } from "@/const";
import BaseAvatar from "@/components/BaseAvatar";
import BaseIcon from "@/components/BaseIcon";
import BaseSpinner from "@/components/BaseSpinner";
import ModalView from "@/components/ModalView";
import PaymentConfirmation from "@/components/PaymentConfirmation";
import { PaymentConfirmationData } from "@/components/PaymentConfirmation/const";
import QuickTipsItem from "@/components/QuickTipsItem";
import SubscriptionItem from "./components/SubscriptionItem.vue";

export default defineComponent({
  name: "UserSubscribeModal",
  components: {
    QuickTipsItem,
    PaymentConfirmation,
    BaseAvatar,
    BaseIcon,
    SubscriptionItem,
    ModalView,
    BaseSpinner,
  },
  props: {
    show: { type: Boolean, required: true },
    user: { type: Object as PropType<UserPublicData>, required: true },
    outerProcessing: { type: Boolean, default: false },
    title: { type: String as PropType<TranslateResult | string>, default: $t("Choose subscription") },
    subscriptionLevelId: { type: String },
  },
  setup(props, { emit }) {
    const { authUser } = useAuthUser();
    const { publicPaymentSystems } = useSystemSettings();
    const { getMaxCurrentSubscription, maxCurrentSubscription } = useMaxCurrentSubscription(props.user.id);

    const processingLevelId = ref("");
    const loading = ref(false);
    const subscriptionLevels = ref<SubscriptionLevel[]>([]);
    const subscriptionLevelsInitialAmount = ref<Record<string, number>>({});
    const selectedSubscriptionLevel = ref<SubscriptionLevel | null>(null);
    const maxCurrentSubscriptionLevel = ref<SubscriptionLevel | null>(null);
    const paymentSystemId = ref("");

    const mainCurrency = getMainCurrencyCode();

    const priceData = computed(() => getFullPriceData(selectedSubscriptionLevel.value?.pricePackage));
    const currentPriceData = computed<FullPriceData>(() => {
      const currentCost =
        subscriptionLevelsInitialAmount.value[`${selectedSubscriptionLevel.value?.id}_${paymentSystemId.value}`];
      if (!currentCost) return priceData.value;
      return {
        ...priceData.value,
        cost: currentCost,
        price: formatMoneyString(currentCost, { currencyCode: getMainCurrencyCode() }),
      };
    });
    const showUpgradeInfo = computed(
      () => !!maxCurrentSubscriptionLevel.value && currentPriceData.value.cost !== priceData.value.cost
    );
    const showPurchaseConfirm = computed(() => !!selectedSubscriptionLevel.value);

    const subscriptionDetails = computed<SubscriptionDetails>(() => ({
      subscriptionSubject: {
        subjectType: RecurringBillingScheduleSubjectTypeEnum.SUBSCRIPTION,
        subjectId: selectedSubscriptionLevel.value?.id ?? "0",
      },
      pricePackageId: selectedSubscriptionLevel.value?.pricePackageId ?? "0",
      price: currentPriceData.value.price,
      paymentSystemId: paymentSystemId.value ?? "",
    }));

    const { performPurchaseSubscription } = usePurchaseSubscription(selectedSubscriptionLevel, subscriptionDetails);

    const filteredSubscriptionLevels = computed(() => {
      const result = subscriptionLevels.value.filter(i => i.contentCount !== 0);
      if (!props.subscriptionLevelId) return result;
      const amount = result.find(i => i.id === props.subscriptionLevelId)?.pricePackage?.amount ?? 0;
      return result.filter(i => (i.pricePackage?.amount ?? 0) >= amount);
    });

    const handleSubscribe = useAuthorisedOnly((newSelectedSubscriptionLevel: SubscriptionLevel) => {
      if (processingLevelId.value) return;
      selectedSubscriptionLevel.value = newSelectedSubscriptionLevel;
    });

    const cancelUpgradeLevel = () => {
      selectedSubscriptionLevel.value = null;
      processingLevelId.value = "";
    };

    const subscribe = async (data: PaymentConfirmationData) => {
      if (!selectedSubscriptionLevel.value) return;
      try {
        processingLevelId.value = selectedSubscriptionLevel.value.id;
        paymentSystemId.value = data.paymentSystemId ?? "";
        const response = await performPurchaseSubscription(data.fromBalance);
        if (response) {
          emitter.emit(EVENTS.CONTENT_FEED_PURCHASED, { userId: props.user.id });
          emitter.emit(EVENTS.NOTIFICATION_SHOW, {
            text: $t("Subscribed to {0} for {1}", [
              props.user.screenName,
              data.fromBalance ? currentPriceData.value.price : currentPriceData.value.creditPrice,
            ]),
            image: { user: props.user },
            button: { text: $t("Close"), cb: () => {} },
            position: "top",
          });
          yMetrics.paymentSubscribe();
          emit("close");
        }
      } finally {
        cancelUpgradeLevel();
      }
    };

    const hasSubscription = computed(() => {
      return filteredSubscriptionLevels.value.some(
        i =>
          maxCurrentSubscriptionLevel.value &&
          maxCurrentSubscriptionLevel.value.pricePackage &&
          i.pricePackage?.amount === maxCurrentSubscriptionLevel.value?.pricePackage.amount
      );
    });

    const isLevelButtonDisabled = (level: SubscriptionLevel) => {
      return (
        maxCurrentSubscriptionLevel.value?.pricePackage &&
        level.pricePackage &&
        level.pricePackage.amount <= maxCurrentSubscriptionLevel.value.pricePackage.amount
      );
    };

    const isActiveSubscriptionLevel = (level: SubscriptionLevel) => {
      return (
        maxCurrentSubscriptionLevel.value?.pricePackage &&
        level.pricePackage &&
        level.pricePackage.amount === maxCurrentSubscriptionLevel.value.pricePackage.amount
      );
    };

    const getInitialCost = async (levelId: string, paymentSystemId: string) => {
      if (!authUser.value) return;
      const amount = await providers.SubscriptionUpgradeProvider.initialAmountFind({
        data: { currency: mainCurrency, paymentSystemId, levelId },
      });
      const key = `${levelId}_${paymentSystemId}`;
      set(subscriptionLevelsInitialAmount.value, key, amount);
    };

    const getInitialCostForAll = async () => {
      if (!maxCurrentSubscriptionLevel.value?.pricePackage) return;
      const currentLevelAmount = maxCurrentSubscriptionLevel.value.pricePackage.amount;
      const systemIds = getAvailableSystems(publicPaymentSystems.value, PurchaseType.SUBSCRIPTION).map(
        s => s.system.id
      );
      const promises: Promise<void>[] = [];
      filteredSubscriptionLevels.value.forEach(level => {
        if ((level.pricePackage?.amount ?? 0) > currentLevelAmount) {
          systemIds.forEach(systemId => promises.push(getInitialCost(level.id, systemId)));
        }
      });
      await Promise.all(promises);
    };

    const getSubscriptionLevels = async () => {
      const response = await providers.SubscriptionLevelProvider.find(
        { userIds: [props.user.id] },
        SUBSCRIPTION_LEVEL_FRAGMENT
      );
      if (response.some(level => level.contentCount && level.contentCount > 0)) {
        subscriptionLevels.value = response;
      }
    };

    const getData = async () => {
      try {
        if (props.user.verified) {
          loading.value = true;
          await Promise.all([getMaxCurrentSubscription(), getSubscriptionLevels()]);
          if (subscriptionLevels.value.length > 0) {
            maxCurrentSubscriptionLevel.value =
              subscriptionLevels.value.find(i => i.id === maxCurrentSubscription.value?.levelId) ?? null;
          }
          emit("levelsLoaded", subscriptionLevels.value);
          getInitialCostForAll().then();
        } else {
          emit("levelsLoaded", []);
        }
      } finally {
        loading.value = false;
      }
    };

    getData();

    return {
      processingLevelId,
      loading,
      filteredSubscriptionLevels,
      priceData,
      currentPriceData,
      showUpgradeInfo,
      showPurchaseConfirm,
      hasSubscription,
      paymentSystemId,
      handleSubscribe,
      PurchaseType,
      isActiveSubscriptionLevel,
      isLevelButtonDisabled,
      cancelUpgradeLevel,
      subscribe,
    };
  },
});
